import { PendingContinuousFeedbackRequestDto } from "@coaching-culture/types";
import {
  Button,
  DateText,
  IconToast,
  Panel,
  PanelHeader,
  SortableTable,
  SortableTableColumn,
  Text,
  useToast,
} from "@coaching-culture/ui";
import { useUser } from "auth";
import { UserFlag } from "components/UserFlag";
import { differenceInHours, fromUnixTime } from "date-fns";
import {
  useNudgeContinuousFeedbackRequest,
  usePendingContinuousFeedbackRequests,
} from "queries/continuousFeedback";
import { useMemo } from "react";
import { FaPaperPlane, FaReply } from "react-icons/fa";

const NUDGE_DELAY_HOURS = 24;

/**
 * Pending requests split by direction for the current user: `awaiting` are
 * requests they've sent that nobody has answered yet, `toAnswer` are
 * requests other people are waiting on them for.
 */
export function usePendingFeedbackRequests() {
  const [user] = useUser();
  const { data, isFetched } = usePendingContinuousFeedbackRequests();

  const awaiting = (data ?? []).filter((x) => x.requester.id === user.id);
  const toAnswer = (data ?? []).filter((x) => x.recipient.id === user.id);

  return { awaiting, toAnswer, isFetched };
}

function RequestNudgeButton({
  request,
}: {
  request: PendingContinuousFeedbackRequestDto;
}) {
  const nudge = useNudgeContinuousFeedbackRequest();
  const toast = useToast();

  const oldEnough =
    differenceInHours(new Date(), fromUnixTime(request.sentOn)) >=
    NUDGE_DELAY_HOURS;
  const nudgedRecently =
    request.lastNudgedOn != null &&
    differenceInHours(new Date(), fromUnixTime(request.lastNudgedOn)) <
      NUDGE_DELAY_HOURS;

  const canNudge = oldEnough && !nudgedRecently && !nudge.isLoading;

  const title = nudgedRecently
    ? "A reminder has been sent in the last 24 hours"
    : oldEnough
      ? `Email ${request.recipient.name} a reminder to respond`
      : "You can send a reminder once the request is 24 hours old";

  const send = async () => {
    try {
      await nudge.mutateAsync(request.id);
      toast({
        content: (
          <IconToast
            icon={FaPaperPlane}
            iconColor="primary"
            text="Reminder sent"
          />
        ),
      });
    } catch (err: any) {
      window.alert(
        err.response?.data ?? "Unable to send a reminder right now.",
      );
    }
  };

  return (
    <span title={title}>
      <Button icon={FaPaperPlane} small disabled={!canNudge} onClick={send}>
        {nudgedRecently ? "Reminder sent" : "Send reminder"}
      </Button>
    </span>
  );
}

export function AwaitingFeedbackPanel() {
  const { awaiting, isFetched } = usePendingFeedbackRequests();

  const cols = useMemo<
    SortableTableColumn<PendingContinuousFeedbackRequestDto>[]
  >(
    () => [
      {
        name: "requestedFrom",
        label: "Requested from",
        format: (x) => <UserFlag user={x.recipient} />,
        sortFormat: (x) => x.recipient.name,
      },
      {
        name: "reason",
        label: "What you asked for feedback on",
        format: (x) => <Text>{x.reason}</Text>,
        sortFormat: (x) => x.reason,
      },
      {
        name: "dateRequested",
        label: "Requested",
        format: (x) => <DateText value={x.sentOn} />,
        sortFormat: (x) => x.sentOn,
      },
      {
        name: "lastReminded",
        label: "Last reminded",
        format: (x) =>
          x.lastNudgedOn != null ? (
            <DateText value={x.lastNudgedOn} />
          ) : (
            <Text color="textMuted">Never</Text>
          ),
        sortFormat: (x) => x.lastNudgedOn ?? 0,
      },
      {
        name: "actions",
        label: "",
        format: (x) => <RequestNudgeButton request={x} />,
        headingProps: { width: "180px" },
      },
    ],
    [],
  );

  if (!isFetched || awaiting.length === 0) {
    return null;
  }

  return (
    <Panel mb={3}>
      <PanelHeader p={3}>
        <Text as="h2" m={0} fontSize="title" fontWeight={600}>
          Feedback you're waiting on
        </Text>
      </PanelHeader>
      <SortableTable paged columns={cols} data={awaiting} />
    </Panel>
  );
}

export function RequestsToAnswerPanel() {
  const { toAnswer, isFetched } = usePendingFeedbackRequests();

  const cols = useMemo<
    SortableTableColumn<PendingContinuousFeedbackRequestDto>[]
  >(
    () => [
      {
        name: "requestedBy",
        label: "Requested by",
        format: (x) => <UserFlag user={x.requester} />,
        sortFormat: (x) => x.requester.name,
      },
      {
        name: "reason",
        label: "What they would like feedback on",
        format: (x) => <Text>{x.reason}</Text>,
        sortFormat: (x) => x.reason,
      },
      {
        name: "dateRequested",
        label: "Requested",
        format: (x) => <DateText value={x.sentOn} />,
        sortFormat: (x) => x.sentOn,
      },
      {
        name: "actions",
        label: "",
        format: (x) => (
          <Button
            to={`/solutions/continuous-feedback/send/${x.requester.id}/${x.id}`}
            small
            icon={FaReply}
          >
            Give feedback
          </Button>
        ),
        headingProps: { width: "160px" },
      },
    ],
    [],
  );

  if (!isFetched || toAnswer.length === 0) {
    return null;
  }

  return (
    <Panel mb={3}>
      <PanelHeader p={3}>
        <Text as="h2" m={0} fontSize="title" fontWeight={600}>
          Requests for your feedback
        </Text>
      </PanelHeader>
      <SortableTable paged columns={cols} data={toAnswer} />
    </Panel>
  );
}
